import {BRANDS, LOGIN_CASES} from 'const'
import config from 'config'
import {getLang, getLoginCase} from 'utils/stringUtils'
import {isProd} from 'utils/envUtils'
import {getDefaultConfig, getFavicon, getFooterLogosByBrandList, getHeaderLogo} from '../commonHelper'
import {FOOTER_LOGO_TYPES} from 'const/logos'
import {COLORS, typography, FONTS} from './typography'

const isSpecificLoginCase = getLoginCase(window.location.search) === LOGIN_CASES.BLICK.SUMMITS
const getSpecificOrDefault = (specificValue, defaultValue) => (isSpecificLoginCase ? specificValue : defaultValue)

const lang = getLang(window.location.search) || 'de'
const brandUrl = `https://www.blick.ch/${lang === 'fr' ? 'fr/' : ''}`
const authorizeRedirectURL = `https://auth${!isProd() ? '.stg' : ''}.ws.blick.ch/authorize?login_case=email_only`

export default {
  brand: {
    name: BRANDS.blick,
    url: brandUrl,
    favicon: getFavicon(BRANDS.blick),
    fontColor: COLORS.black,
    authorizeRedirectURL,
  },
  config: {
    ...getDefaultConfig(),
    useCircularSocialLogin: true,
    marketingOnePager: ({loginCase}) =>
      [
        LOGIN_CASES.EMAIL_ONLY,
        LOGIN_CASES.BLICK.EMAIL_ONLY_SPORT,
        LOGIN_CASES.BLICK.BLICKPLUS_PIANO,
        LOGIN_CASES.BLICK.BLICKPLUS_ABOSHOP,
      ].includes(loginCase),
    newsletterOnePager: ({loginCase}) =>
      [LOGIN_CASES.EMAIL_ONLY, LOGIN_CASES.BLICK.EMAIL_ONLY_SPORT, LOGIN_CASES.BLICK.BLICKPLUS_ABOSHOP].includes(
        loginCase
      ),
    admeiraOptInOnePager: true,
    marketingConsent: ({loginCase}) => loginCase !== LOGIN_CASES.BLICK.SUMMITS,
    onePager: ({loginCase}) =>
      [
        LOGIN_CASES.EMAIL_ONLY,
        LOGIN_CASES.BLICK.EMAIL_ONLY_SPORT,
        LOGIN_CASES.BLICK.SUMMITS,
        LOGIN_CASES.BLICK.BLICKPLUS_PIANO,
        LOGIN_CASES.BLICK.BLICKPLUS_ABOSHOP,
        LOGIN_CASES.BLICK.EMAIL_ONLY_MYSERVICE,
      ].includes(loginCase),
    newsletterConsent: true,
    subtitleWithFAQLanding: true,
    emailOnlyRegister: ({loginCase}) => [LOGIN_CASES.BLICK.BLICKPRINT_ABOSHOP].includes(loginCase),
    showProgressBar: ({loginCase}) =>
      [LOGIN_CASES.BLICK.BLICKPLUS_PIANO, LOGIN_CASES.BLICK.BLICKPLUS_ABOSHOP].includes(loginCase),
    showRingierBanner: ({loginCase}) =>
      ![
        LOGIN_CASES.BLICK.BLICKPLUS_PIANO,
        LOGIN_CASES.BLICK.BLICKPLUS_ABOSHOP,
        LOGIN_CASES.BLICK.EMAIL_ONLY_MYSERVICE,
      ].includes(loginCase),
  },
  fontConfig: {
    custom: {
      urls: [`${config.url}/fonts/codeNext/index.css`, `${config.url}/fonts/inter/index.css`],
    },
  },
  font: {
    family: typography.copy.body.fontFamily,
    fontWeight: typography.copy.body.fontWeight,
    boldFamily: typography.copy.bodyBold.fontFamily,
    boldFontWeight: typography.copy.bodyBold.fontWeight,
    size: typography.copy.body.fontSize,
    height: typography.copy.body.lineHeight,
    mobile: {
      size: typography.copy.body.fontSize,
      height: typography.copy.body.lineHeight,
    },
    portrait: {
      size: typography.copy.body.fontSize,
      height: typography.copy.body.lineHeight,
    },
    color: {
      dark: typography.copy.body.color,
      light: typography.copy.body.color,
    },
  },
  secondaryFont: {
    size: typography.copy.tiny.fontSize,
    height: typography.copy.tiny.lineHeight,
    mobile: {
      size: typography.copy.tiny.fontSize,
      height: typography.copy.tiny.lineHeight,
    },
    portrait: {
      size: typography.copy.tiny.fontSize,
      height: typography.copy.tiny.lineHeight,
      backlink: typography.copy.tiny.fontSize,
    },
  },
  title: {
    family: typography.heading.small.fontFamily,
    size: typography.heading.small.fontSize,
    height: typography.heading.small.lineHeight,
    weight: typography.heading.small.fontWeight,
    margin: '0 0 24px 0',
    mobile: {
      size: typography.heading.small.fontSize,
      height: typography.heading.small.lineHeight,
    },
    portrait: {
      size: typography.heading.large.fontSize,
      height: typography.heading.large.lineHeight,
    },
  },
  ringierBanner: {
    fontSize: '16px',
    linkFontSize: '14px',
    padding: '20px',
    lineHeight: '24px',
    benefitsIcon: `${config.url}/assets/brands/blick/icon-check-banner.svg`,
    backgroundColor: COLORS.grey100,
    linkColor: COLORS.black,
    linkHoverColor: COLORS.redDark,
    titleFontSize: typography.copy.title.fontSize,
    titleFontWeight: typography.copy.title.fontWeight,
    titleFontFamily: typography.copy.title.fontFamily,
    titleColor: typography.copy.title.color,
    messageFamily: FONTS.REGULAR,
    iconWidth: '24px',
    iconHeight: '24px',
    landscape: {
      fontSize: '16px',
    },
    onelogLogo: {
      src: `${config.url}/assets/logo-onelog-black.svg`,
      height: '28px',
      width: '132px',
      margin: '0 0 8px 0',
    },
    onelogTitle: {
      fontSize: typography.copy.tinyBold.fontSize,
      fontWeight: typography.copy.tinyBold.fontWeight,
      fontFamily: typography.copy.tinyBold.fontFamily,
      lineHeight: typography.copy.tinyBold.lineHeight,
      color: COLORS.black,
      margin: '0 0 20px 0',
    },
    onelogSection: {
      padding: '40px 0 0 0',
      borderTop: `1px solid ${COLORS.grey400}`,
    },
  },
  otherBrandsLogos: getFooterLogosByBrandList([BRANDS.blick, BRANDS.beobachter, BRANDS.srg, BRANDS.twentymin]),
  note: {
    family: FONTS.BOLD,
    termsTitle: {
      fontWeight: 'bold',
      fontFamily: FONTS.BOLD,
    },
  },
  background: {
    light: '#ffffff',
    dark: ' #47484a',
  },
  notification: {
    family: FONTS.REGULAR,
    success: COLORS.success,
    error: COLORS.error,
    pageError: COLORS.error,
    warning: COLORS.warning,
    info: COLORS.info,
    icon: {
      width: '24px',
      height: '24px',
      mobile: {
        width: '32px',
        height: '32px',
      },
    },
    pageMessage: {
      size: '16px',
      height: '22px',
      sizeMobile: '14px',
      heightMobile: '20px',
    },
  },
  buttons: {
    height: '48px',
    primary: {
      border: `1px solid ${COLORS.red}`,
      color: COLORS.white,
      background: COLORS.red,
      hoverBorder: `1px solid ${COLORS.redDark}`,
      hoverBackground: COLORS.redDark,
      focusBorder: `1px solid ${COLORS.redDark}`,
      focusBackground: COLORS.redDark,
      activeBorder: `1px solid ${COLORS.redDark}`,
      activeBackground: COLORS.red,
      textStyles: {
        family: FONTS.BOLD,
        size: '16px',
        weight: 700,
      },
    },
    secondary: {
      border: `1px solid ${COLORS.red}`,
      color: COLORS.red,
      background: COLORS.white,
      hoverBorder: `1px solid ${COLORS.redDark}`,
      hoverColor: COLORS.redDark,
      hoverBackground: COLORS.white,
      activeBorder: `1px solid ${COLORS.redDark}`,
      focusBorder: `1px solid ${COLORS.redDark}`,
      focusColor: COLORS.redDark,
      focusBackground: COLORS.redDark,
      activeColor: COLORS.redDark,
      activeBackground: COLORS.white,
      dotsColor: COLORS.red,
      textStyles: {
        family: FONTS.BOLD,
        size: '16px',
        weight: 700,
      },
    },
    disabled: {
      border: `1px solid${COLORS.grey200}`,
      color: COLORS.grey400,
      background: COLORS.grey200,
      hoverBorder: `1px solid${COLORS.grey200}`,
      hoverBackground: COLORS.grey200,
      activeBorder: `1px solid${COLORS.grey200}`,
      activeBackground: COLORS.grey200,
    },
    touchId: {
      border: `1px solid ${COLORS.red}`,
      color: COLORS.white,
      background: COLORS.red,
      hoverBorder: `1px solid ${COLORS.redDark}`,
      hoverBackground: COLORS.redDark,
      focusBorder: `1px solid ${COLORS.redDark}`,
      focusBackground: COLORS.redDark,
      activeBorder: `1px solid ${COLORS.redDark}`,
      activeBackground: COLORS.redDark,
      icon: 'icon-touchid.svg',
      iconStyles: {padding: '12px', marginLeft: '0'},
      textStyles: {
        size: '16px',
        family: FONTS.BOLD,
        weight: 700,
      },
      descriptionStyles: {
        fontFamily: FONTS.REGULAR,
        fontSize: '14px',
      },
    },
    faceId: {
      border: `1px solid ${COLORS.red}`,
      color: COLORS.white,
      background: COLORS.red,
      hoverBorder: `1px solid ${COLORS.redDark}`,
      hoverBackground: COLORS.redDark,
      focusBorder: `1px solid ${COLORS.redDark}`,
      focusBackground: COLORS.redDark,
      activeBorder: `1px solid ${COLORS.redDark}`,
      activeBackground: COLORS.redDark,
      icon: 'icon-faceid.svg',
      iconStyles: {padding: '12px', marginLeft: '5px'},
      textStyles: {
        size: '16px',
        family: FONTS.BOLD,
        weight: 700,
      },
      descriptionStyles: {
        fontFamily: FONTS.REGULAR,
        fontSize: '14px',
      },
    },
    anyId: {
      border: `1px solid ${COLORS.red}`,
      color: COLORS.white,
      background: COLORS.red,
      hoverBorder: `1px solid ${COLORS.redDark}`,
      hoverBackground: COLORS.redDark,
      focusBorder: `1px solid ${COLORS.redDark}`,
      focusBackground: COLORS.redDark,
      activeBorder: `1px solid ${COLORS.redDark}`,
      activeBackground: COLORS.redDark,
      textStyles: {
        size: '16px',
        family: FONTS.BOLD,
        weight: 700,
      },
      descriptionStyles: {
        fontFamily: FONTS.REGULAR,
        fontSize: '14px',
      },
    },
    icon: {
      height: '24px',
      width: '24px',
    },
    text: {
      family: FONTS.BOLD,
      color: COLORS.black,
      size: '16px',
    },
    border: {
      radius: '24px',
    },
  },
  link: {
    default: COLORS.black,
    hoverColor: COLORS.redDark,
    secondary: COLORS.black,
    secondaryHover: COLORS.redDark,
    agreement: COLORS.black,
    isAgreementsLink: true,
    isAgreementsLinkSecondary: true,
    textDecorationHover: 'underline',
    align: 'left',
    alignMobile: 'left',
  },
  logisterSection: {
    registerMarginTop: '24px',
    registerMarginBottom: '24px',
    registerWeight: typography.copy.tiny.fontWeight,
    registerSize: typography.copy.tiny.fontSize,
    subheaderPadding: '12px 20px',
    subheaderFontSize: '16px',
    subheaderLineHeight: '24px',
    subheaderTitleColor: COLORS.black,
    subheaderLinkColor: '#545454',
    subheaderLinkHoverColor: '#FF0000',
    subheaderBackgroundColor: '#F8F8F8',
    noteFontSize: {
      default: '12px',
      portrait: '14px',
    },
    title: {
      textChangeBreakpoint: 'mobile',
      fontSize: {
        smallMobile: typography.heading.small.fontSize,
        mobile: typography.heading.small.fontSize,
        portrait: typography.heading.large.fontSize,
        landscape: typography.heading.large.fontSize,
        desktop: typography.heading.large.fontSize,
      },
      lineHeight: {
        smallMobile: typography.heading.small.lineHeight,
        mobile: typography.heading.small.lineHeight,
        portrait: typography.heading.large.lineHeight,
        landscape: typography.heading.large.lineHeight,
        desktop: typography.heading.large.lineHeight,
      },
    },
    subtitle: {
      color: COLORS.grey700,
      fontSize: {
        default: '16px',
        portrait: '16px',
      },
      lineHeight: '24px',
      margin: '-8px 0 20px',
      link: {
        color: COLORS.grey700,
        hoverColor: COLORS.redDark,
      },
      display: {
        portrait: 'none',
      },
    },
  },
  form: {
    border: '',
    fieldBackground: '',
    padding: '0 0 24px 0',
    marginBottom: '0',
    loginSubmitMarginTop: 16,
    note: {
      fontSize: '18px',
    },
  },
  logisterForm: {
    padding: '28px 0 0 0',
    submitButton: 'primary',
    submitButtonMargin: '36px 0 0 0',
    forgetLinkFloat: 'right',
  },
  loginForm: {
    submitButtonMargin: 36,
    title: {
      margin: '0 0 48px',
    },
    forgetLink: {
      wrapper: {
        defaultMarginBottom: '20px',
        errorMarginBottom: '22px',
      },
      link: {
        fontSize: typography.copy.tiny.fontSize,
      },
    },
  },
  registerPage: {
    backLinkMargin: {
      default: '0 0 24px',
      portrait: '0 0 24px',
    },
    titleMargin: {
      default: '0 0 24px',
      portrait: '0 0 24px',
    },
    submitButtonMarginTop: 12,
    subtitle: {
      fontFamily: typography.heading.subheading.fontFamily,
      fontSize: typography.heading.subheading.fontSize,
      lineHeight: typography.heading.subheading.lineHeight,
      fontWeight: typography.heading.subheading.fontWeight,
      margin: '0 0 48px',
    },
  },
  emailVerifyPage: {
    note: {
      fontFamily: FONTS.BOLD,
    },
  },
  field: {
    error: {
      fontColor: COLORS.error,
      size: typography.copy.tiny.fontSize,
      height: typography.copy.tiny.lineHeight,
      padding: '0',
      margin: '6px 0 0',
    },
    label: {
      color: typography.label.color,
      errorColor: COLORS.error,
      disabledColor: COLORS.grey400,
      family: typography.label.fontFamily,
      fontWeight: typography.label.fontWeight,
      fontSize: typography.label.fontSize,
      fontHeight: typography.label.lineHeight,
      bottomMargin: '6px',
    },
    edging: {
      backgroundColor: '',
    },
    optional: {
      fontSize: typography.optional.fontSize,
      height: typography.optional.lineHeight,
      color: typography.optional.color,
      family: typography.optional.fontFamily,
      weight: typography.optional.fontWeight,
    },
  },
  input: {
    borderRadius: '24px',
    border: `1px solid ${COLORS.grey400}`,
    focusBorder: `1px solid ${COLORS.grey400}`,
    errorBorder: `1px solid ${COLORS.error}`,
    hoverBorder: `1px solid ${COLORS.grey400}`,
    padding: '8px 24px',
    height: '48px',
    fontSize: '16px',
    email: {
      backgroundColor: COLORS.grey200,
      border: `1px solid ${COLORS.grey200}`,
      borderRadius: '24px',
      margin: '0 12px 0 24px',
      color: COLORS.grey700,
      button: {
        height: '32px',
        right: '12px',
        backgroundColor: COLORS.grey200,
        hoverBackgroundColor: COLORS.grey200,
        color: COLORS.red,
        fontFamily: FONTS.SEMI_BOLD,
        fontSize: '14px',
        border: `1px solid ${COLORS.red}`,
        borderRight: `1px solid ${COLORS.red}`,
        hoverColor: COLORS.redDark,
        hoverBorder: `1px solid ${COLORS.redDark}`,
        hoverBorderRight: `1px solid ${COLORS.redDark}`,
        borderRadius: '16px',
      },
    },
    password: {
      color: COLORS.black,
      hoverColor: COLORS.redDark,
      size: '14px',
      height: '18px',
      top: '15px',
      right: '22px',
    },
    loading: {
      top: '7px',
    },
    placeholder: {
      color: COLORS.grey700,
      fontSize: '16px',
      lineHeight: '22px',
    },
  },
  checkbox: {
    icon: {
      height: '24px',
      width: '24px',
      top: '0px',
      right: '12px',
      image: 'brands/blick/form/checkbox.svg',
      checkedImage: 'brands/blick/form/checkbox-checked.svg',
      hoverImage: 'brands/blick/form/checkbox-hover.svg',
      errorImage: 'brands/blick/form/checkbox-error.svg',
      disabledImage: 'brands/blick/form/checkbox-disabled.svg',
    },
    errorMargin: '20px -20px -16px',
    padding: '20px',
    labelFontHeight: '22px',
    errorPadding: '4px 20px 0',
    group: {
      label: {
        marginBottom: '8px',
        padding: '0',
        backgroundColor: '',
      },
      fontSize: '16px',
      marginBottom: '6px',
      padding: '0 24px',
      backgroundColor: COLORS.grey100,
      note: {
        fontSize: '14px',
        marginLeft: '36px',
      },
      wrapper: {
        marginBottom: '36px',
      },
    },
  },
  edging: {
    borderRadius: '0px',
    backgroundColor: COLORS.grey100,
  },
  select: {
    borderRadius: '24px',
    open: {
      borderRadius: '24px 24px 0 0',
    },
    icon: {
      width: '16px',
      height: '16px',
      image: 'brands/blick/icon-arrow-down.svg',
      focusImage: 'brands/blick/icon-arrow-down-active.svg',
      alt_image: 'brands/blick/icon-arrow-down.svg',
    },
    autosuggest: {
      open: {
        borderRadius: '24px 24px 0 0',
      },
      menu: {
        boxShadow: 'none',
        list: {
          margin: '0 0 24px 0',
        },
      },
    },
    menu: {
      border: `1px solid ${COLORS.grey400}`,
      borderTop: `1px solid ${COLORS.grey400}`,
      marginTop: '-1px',
      borderRadius: '0 0 24px 24px',
      padding: '0 0 24px 0',
      list: {
        boxShadow: 'none',
        borderRadius: '0',
      },
      option: {
        focusBorder: '0',
        focusBackground: COLORS.ultraLight4,
      },
    },
  },
  radio: {
    fontSize: '14px',
    icon: {
      top: '0px',
      width: '24px',
      height: '24px',
      image: 'brands/blick/form/radio.svg',
      checkedImage: 'brands/blick/form/radio-active.svg',
      hoverImage: 'brands/blick/form/radio-hover.svg',
      errorImage: 'brands/blick/form/radio-error.svg',
      disabledImage: 'brands/blick/form/radio-disabled.svg',
    },
    errorMargin: '6px 0 0',
    labelFontHeight: '22px',
  },
  errorPage: {
    font: {
      height: '18px',
      portrait: {
        height: '30px',
      },
    },
  },
  inputNote: {
    color: COLORS.black,
    size: typography.copy.tiny.fontSize,
    height: typography.copy.tiny.lineHeight,
    marginTop: '6px',
  },
  separator: {
    background: COLORS.grey400,
    color: COLORS.black,
    height: '1px',
    fontSize: typography.copy.tiny.fontSize,
    lineHeight: typography.copy.tiny.fontSize,
    portraitMargin: '40px 0',
  },
  tab: {
    fontSize: '18px',
    desktop: {
      focusBorder: 'solid 4px #000000',
      bottomMargin: '-4px',
      height: '80px',
    },
    mobile: {
      icon: {
        border: 'solid 2px #ACACAC',
        width: '14px',
        height: '14px',
        color: '#000000',
      },
    },
  },
  shell: {
    header: {
      hideRingierOnMobile: getSpecificOrDefault(true, false),
      height: {
        default: '80px',
        onScroll: '56px',
        withEmail: '102px',
      },
      color: COLORS.black,
      backgroundColor: COLORS.white,
      border: {
        portrait: {
          width: '0 0 1px 0',
          color: COLORS.grey400,
          style: 'solid',
        },
      },
      padding: '18px 20px',
      poweredLogo: {
        src: 'brands/onelog/poweredBy/poweredby-onelog-grey.png',
        height: '31px',
      },
      icon: {
        image: getHeaderLogo(BRANDS.blick),
        height: '34px',
        href: brandUrl,
        portrait: {
          height: '36px',
        },
      },
      logoMarginRight: {
        default: '0',
        portrait: '0',
      },
      logo: {
        centered: true,
        height: {
          default: '36px',
          small: '34px',
          onScroll: '34px',
        },
      },
      languageSwitcher: {
        color: '#000',
        activeColor: '#e20000',
        backgroundColor: '#FFFFFF',
        menu: {
          fontSize: '14px',
          lineHeight: '22px',
          color: '#000',
          hoverBackgroundColor: '#cdd5db',
        },
      },
      menu: {
        default: {
          color: '#000000',
          fontSize: '20px',
        },
        active: {
          color: '#FFFFFF',
          backgroundColor: '#e20000',
          hoverBackgroundColor: '#ff0000',
          selectedBackgroundColor: '#919191',
          separatorColor: '#2C3B47',
        },
      },
    },
    footer: {
      tenants: {
        logoColor: FOOTER_LOGO_TYPES.black,
        color: COLORS.black,
        hoverColor: COLORS.redDark,
        backgroundColor: COLORS.grey100,
        fontSize: '14px',
        lineHeight: '21px',
        separatorColor: COLORS.grey700,
        hideOn: {
          tablet: true,
          desktop: true,
        },
      },
      copyright: {
        color: COLORS.white,
        hoverColor: COLORS.white,
        backgroundColor: COLORS.grey700,
        fontSize: '14px',
        lineHeight: '21px',
        separatorColor: COLORS.grey700,
      },
    },
  },
  consent: {
    checkbox: {
      marginBottom: '8px',
      paddingBottom: '16px',
    },
  },
  phoneNumber: {
    labelInsideView: true,
    container: {
      backgroundColor: COLORS.grey100,
      padding: '20px',
    },
    select: {
      borderRadius: '24px 0 0 24px',
      open: {
        borderRadius: '24px 0 0 0',
      },
      menu: {
        padding: '0 0 24px 0',
        borderRadius: '0 0 24px 24px',
      },
    },
  },
  address: {
    padding: '0px',
    inputBackgroundColor: '',
    formFieldMarginBottom: '0px',
    checkboxPadding: '20px 20px 16px 20px',
    link: {
      fontSize: '14px',
      lineHeight: '20px',
    },
  },
  webauthn: {
    dropdown: {
      color: COLORS.black,
      hoverColor: COLORS.redDark,
      fontSize: '16px',
    },
    logisterForm: {
      marginBottom: '16px',
    },
    setup: {
      title: {
        margin: '0 0 12px',
      },
      description: {
        margin: '0 0 12px',
      },
      text: {
        margin: '0 0 16px',
      },
    },
  },
  mfa: {
    backgroundColor: COLORS.grey100,
    backButtonFontSize: '14px',
    inputPadding: '12px',
    subtitleFontSize: '16px',
    textFontSize: '16px',
    title: {
      margin: '0 0 48px',
    },
    link: {
      color: COLORS.black,
      hoverColor: COLORS.redDark,
      margin: '0 0 12px',
    },
  },
  emailVerifiedPage: {
    subtitle: {
      fontFamily: FONTS.BOLD,
      fontSize: '16px',
      margin: '0 0 36px 0',
      color: COLORS.black,
    },
  },
  languageSwitcher: {
    profileMenuIcon: 'OL-icon-arrow-down-grey.svg',
    profileMenuColor: '#3C5162',
  },
  progressBar: {
    color: '#A1A1A1',
    activeColor: '#111111',
    item: {
      default: {
        color: '#A1A1A1',
        border: '2px solid #A1A1A1',
      },
      active: {
        color: '#E20000',
        border: '2px solid #E20000',
      },
    },
  },
  social: {
    title: {
      fontSize: '16px',
      fontFamily: FONTS.BOLD,
    },
  },
}
